import React from 'react';
import { Subscription } from '../../services/subscriptionService'; // Assuming Subscription type is available
import { useTranslation } from 'react-i18next';

interface SubscriptionCardProps {
  subscription: Subscription; // Using the type from service, can be 'any' if not strictly defined yet
  onEdit: (subscriptionId: string) => void; // Callback when edit is clicked, passing subscription ID
  // Add other props as needed, e.g., onCancel, onRenew, etc.
}

const SubscriptionCard: React.FC<SubscriptionCardProps> = ({ subscription, onEdit }) => {
  const { t } = useTranslation();
  // Basic display - to be expanded significantly
  const planName = subscription.plan?.name || 'N/A';
  const channelName = subscription.channel?.nameChannel || subscription.channel?.name || t('noChannelAssigned'); // Fallback for channel name
  const status = subscription.active ? 'active' : 'suspended';
  const endDate = subscription.endDate ? new Date(subscription.endDate).toLocaleDateString() : 'N/A';

  return (
    <div className="bg-white shadow-lg rounded-lg p-6 mb-4 border border-gray-200 hover:shadow-xl transition-shadow duration-200">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-xl font-semibold text-gray-800">{planName}</h3>
          <p className={`text-sm ${subscription.channel ? 'text-gray-500' : 'text-orange-600 font-medium'}`}>
            {t('channelLabel')}: {channelName}
          </p>
          {!subscription.channel && (
            <span className="inline-block mt-1 px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800">
              {t('requiresChannel')}
            </span>
          )}
        </div>
        <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
          status.toLowerCase() === 'active' ? 'bg-green-100 text-green-800' :
          status.toLowerCase() === 'suspended' ? 'bg-red-100 text-red-800' :
          'bg-yellow-100 text-yellow-800'
        }`}>
          {t(status)}
        </span>
      </div>

      {/* Responsive grid for details */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 mb-4 text-sm">
        <div>
          <p className="text-gray-500">{t('subscriptionId')}:</p>
          <p className="text-gray-700 font-medium truncate" title={subscription.id}>{subscription.id}</p>
        </div>
        <div>
          <p className="text-gray-500">{t('endDate')}:</p>
          <p className="text-gray-700 font-medium">{endDate}</p>
        </div>
        <div>
          <p className="text-gray-500">{t('autoRenewal')}:</p>
          <p className="text-gray-700 font-medium">{subscription.autoRenewal ? t('on') : t('off')}</p>
        </div>
        {/* Add more details as needed, e.g., price, billing period label */}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          onClick={() => onEdit(subscription.id)}
          className="py-2 px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium"
        >
          {t('manageSubscription')}
        </button>
        {/* Add other action buttons like "Cancel Subscription" if needed */}
      </div>
    </div>
  );
};

export default SubscriptionCard;
